import React, { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import { FORM_TEMPLATES } from "./formTemplates";

export default function ChatWidgetLite({ apiBase = "", defaultSurface = "landing", theme = "light" }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [surface, setSurface] = useState(defaultSurface);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [collectingData, setCollectingData] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [userRole, setUserRole] = useState(defaultSurface === "landing" ? "guest" : null);
  const [suggestions, setSuggestions] = useState([
    { text: "Order Issue", message: "I need help with my order" },
    { text: "Track Order", message: "Track my order" },
    { text: "Course Inquiry", message: "Tell me about academy courses" },
    { text: "Store Products", message: "What products are available?" },
  ]);

  const messages = useMemo(() => history, [history]);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Check user login status on mount for store/academy surfaces
  useEffect(() => {
    if (defaultSurface !== "landing" && userRole === null) {
      async function checkLoginStatus() {
        try {
          const authUrl = apiBase
            ? `${apiBase.replace(/\/$/, "")}/api/auth`
            : "/api/auth";
          const res = await fetch(authUrl, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            setUserRole(data.isLoggedIn ? "user" : "guest");
            if (data.isLoggedIn && data.email) {
              setFormData((prev) => ({ ...prev, email: data.email }));
            }
          } else {
            setUserRole("guest");
          }
        } catch (e) {
          setUserRole("guest");
          setErr("Failed to verify login status.");
        }
      }
      checkLoginStatus();
    }
  }, [defaultSurface, apiBase, userRole]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, collectingData]);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Generate session ID
  function generateSessionId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  async function sendMessage(text) {
    const t = (text || "").trim();
    if (!t || sending) return;

    const newHistory = [...history, { role: "user", content: t }];
    setHistory(newHistory);
    setInput("");
    setSending(true);
    setErr("");

    const chatUrl = apiBase
      ? `${apiBase.replace(/\/$/, "")}/api/chat`
      : "/api/chat";

    try {
      const res = await fetch(chatUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newHistory, surface }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.collectingUserData && data.intent) {
        let intent = data.intent;
        // Enhanced intent mapping
        if (intent === "order_issue") {
          if (data.requiredFields?.includes("refundReason")) intent = "refund_request";
          else if (data.requiredFields?.includes("defectDescription")) intent = "product_defective";
          else if (data.requiredFields?.includes("orderNumber")) intent = "order_tracking";
          else intent = "general_inquiry";
        }

        const role = FORM_TEMPLATES[intent] && FORM_TEMPLATES[intent][userRole || "guest"]
          ? userRole || "guest"
          : "guest";

        if (FORM_TEMPLATES[intent]) {
          setActiveForm({ intent, role });
          setCollectingData(true);
          setSuggestions([]); // Hide suggestions during form collection
        } else {
          setErr(`Invalid form intent: ${data.intent}. Defaulting to general inquiry.`);
          setActiveForm({ intent: "general_inquiry", role: "guest" });
          setCollectingData(true);
          setSuggestions([]);
        }
      } else {
        setHistory((h) => [
          ...h,
          {
            role: "assistant",
            content: data.answer || "(no answer)",
            sources: data.sources || [],
            ticketId: data.ticketId,
          },
        ]);
        setCollectingData(false);
        setActiveForm(null);
      }

      if (data.suggestions) {
        setSuggestions(data.suggestions);
      }
    } catch (e) {
      setErr(e?.message || String(e));
      setHistory((h) => [
        ...h,
        { role: "assistant", content: "Sorry — the chat request failed." },
      ]);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  function handleFormChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  async function submitForm() {
    if (sending) return;

    // Validate required fields
    const formConfig = FORM_TEMPLATES[activeForm.intent][activeForm.role];
    const missingFields = formConfig.fields
      .filter((field) => field.required && !formData[field.name]?.trim())
      .map((field) => field.label);

    if (missingFields.length > 0) {
      setErr(`Please fill in the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    setSending(true);
    setErr("");

    const chatUrl = apiBase
      ? `${apiBase.replace(/\/$/, "")}/api/chat`
      : "/api/chat";

    try {
      const res = await fetch(chatUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: history,
          surface,
          formData,
          formIntent: activeForm.intent,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      setHistory((h) => [
        ...h,
        { role: "user", content: "Form submitted" },
        {
          role: "assistant",
          content:
            data.answer ||
            (FORM_TEMPLATES[activeForm.intent] &&
            FORM_TEMPLATES[activeForm.intent][activeForm.role]
              ? FORM_TEMPLATES[activeForm.intent][activeForm.role].completion
              : "Form submitted successfully."),
          sources: data.sources || [],
          ticketId: data.ticketId,
        },
      ]);
      setCollectingData(false);
      setActiveForm(null);
      setFormData({});
      setSuggestions([
        { text: "Order Issue", message: "I need help with my order" },
        { text: "Track Order", message: "Track my order" },
        { text: "Course Inquiry", message: "Tell me about academy courses" },
        { text: "Store Products", message: "What products are available?" },
      ]);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSending(false);
    }
  }

  async function cancelForm() {
    setCollectingData(false);
    setActiveForm(null);
    setFormData({});
    setSuggestions([
      { text: "Order Issue", message: "I need help with my order" },
      { text: "Track Order", message: "Track my order" },
      { text: "Course Inquiry", message: "Tell me about academy courses" },
      { text: "Store Products", message: "What products are available?" },
    ]);
    setHistory((h) => [
      ...h,
      { role: "assistant", content: "Form cancelled. How else can I assist?" },
    ]);

    // Clear session data on backend
    const chatUrl = apiBase
      ? `${apiBase.replace(/\/$/, "")}/api/chat/clear-form-session`
      : "/api/chat/clear-form-session";
    try {
      await fetch(chatUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: generateSessionId() }),
      });
    } catch (e) {
      console.error("Failed to clear session:", e);
    }
  }

  function renderMarkdown(text) {
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: false,
      mangle: false,
    });
    return { __html: marked.parse(text) };
  }

  const isDark = theme === "dark";

  const styles = {
    root: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: isDark ? "#020102" : "#fff",
      color: isDark ? "#fff" : "#111",
      fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
      fontSize: 14,
      lineHeight: 1.4,
    },
    list: {
      flex: 1,
      overflowY: "auto",
      padding: "12px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      background: isDark ? "rgba(255, 255, 255, 0.02)" : "#fff",
      border: `1px solid ${isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb"}`,
      borderRadius: 15,
    },
    message: {
      marginBottom: 6,
      animation: "slideIn 0.3s ease-out",
    },
    bubbleUser: {
      alignSelf: "flex-end",
      background: isDark ? "linear-gradient(135deg, #ff6600, #ff8533)" : "#111",
      color: "#fff",
      borderRadius: "18px 18px 4px 18px",
      padding: "8px 12px",
      maxWidth: "80%",
      fontSize: 14,
      lineHeight: 1.4,
      wordWrap: "break-word",
    },
    bubbleBot: {
      alignSelf: "flex-start",
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#f8f9fa",
      border: `1px solid ${isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb"}`,
      color: isDark ? "#fff" : "#2c2c2c",
      borderRadius: "18px 18px 18px 4px",
      padding: "8px 12px",
      maxWidth: "80%",
      fontSize: 14,
      lineHeight: 1.4,
      wordWrap: "break-word",
    },
    successMessage: {
      background: isDark ? "rgba(40, 167, 69, 0.1)" : "rgba(40, 167, 69, 0.1)",
      border: `2px solid ${isDark ? "rgba(40, 167, 69, 0.3)" : "rgba(40, 167, 69, 0.3)"}`,
      color: isDark ? "#66d9a2" : "#28a745",
    },
    ticketId: {
      fontWeight: 600,
      color: isDark ? "#FF9933" : "#FF6600",
      background: isDark ? "rgba(255, 102, 0, 0.1)" : "rgba(255, 102, 0, 0.1)",
      padding: "2px 6px",
      borderRadius: 4,
      marginTop: 6,
      fontSize: 11,
    },
    sourcesWrap: {
      marginTop: 8,
      paddingTop: 6,
      borderTop: `1px solid ${isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb"}`,
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    },
    source: {
      fontSize: 10,
      color: isDark ? "#FF9933" : "#FF6600",
      textDecoration: "none",
      padding: "2px 6px",
      background: isDark ? "rgba(255, 102, 0, 0.1)" : "rgba(255, 102, 0, 0.1)",
      borderRadius: 8,
      transition: "all 0.2s ease",
    },
    sourceHover: {
      background: isDark ? "rgba(255, 102, 0, 0.2)" : "rgba(255, 102, 0, 0.2)",
      transform: "scale(1.05)",
    },
    error: {
      textAlign: "center",
      background: isDark ? "rgba(220, 53, 69, 0.1)" : "rgba(220, 53, 69, 0.1)",
      border: `1px solid ${isDark ? "rgba(220, 53, 69, 0.3)" : "rgba(220, 53, 69, 0.3)"}`,
      color: isDark ? "#ffaaaa" : "#dc2626",
      fontSize: 12,
      padding: "6px 10px",
      borderRadius: 8,
      margin: "6px 0",
    },
    composer: {
      borderTop: `1px solid ${isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb"}`,
      padding: "12px",
      background: isDark ? "rgba(2, 1, 2, 0.3)" : "#fafafa",
    },
    row: {
      display: "flex",
      gap: 10,
      alignItems: "flex-end",
    },
    textarea: {
      flex: 1,
      resize: "none",
      border: `2px solid ${isDark ? "rgba(255, 102, 0, 0.3)" : "#d1d5db"}`,
      borderRadius: 20,
      padding: "8px 14px",
      minHeight: 20,
      maxHeight: 80,
      fontFamily: "inherit",
      fontSize: 14,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#fff",
      color: isDark ? "#fff" : "#374151",
      outline: "none",
      transition: "all 0.3s ease",
      lineHeight: 1.4,
    },
    textareaFocus: {
      borderColor: isDark ? "#FF6600" : "#FF6600",
      boxShadow: isDark ? "0 0 15px rgba(255, 102, 0, 0.3)" : "0 0 15px rgba(255, 102, 0, 0.3)",
      background: isDark ? "rgba(255, 255, 255, 0.08)" : "#fff",
    },
    send: {
      border: "none",
      borderRadius: 20,
      padding: "8px 18px",
      background: isDark ? "linear-gradient(135deg, #FF6600, #FF8533)" : "linear-gradient(135deg, #FF6600, #FF8533)",
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
      opacity: sending ? 0.6 : 1,
      transition: "all 0.3s ease",
      minWidth: 60,
      height: 36,
    },
    sendDisabled: {
      background: isDark ? "rgba(255, 102, 0, 0.3)" : "rgba(255, 102, 0, 0.3)",
      cursor: "not-allowed",
    },
    hint: {
      marginTop: 8,
      fontSize: 11,
      color: isDark ? "rgba(255, 255, 255, 0.5)" : "#6b7280",
      textAlign: "center",
    },
    suggestions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginBottom: 10,
    },
    suggestionChip: {
      padding: "6px 12px",
      background: isDark ? "rgba(255, 102, 0, 0.1)" : "#f3f4f6",
      border: `1px solid ${isDark ? "rgba(255, 102, 0, 0.3)" : "#e5e7eb"}`,
      borderRadius: 16,
      fontSize: 12,
      cursor: "pointer",
      color: isDark ? "#FF6600" : "#FF6600",
      transition: "all 0.3s ease",
      fontWeight: 500,
    },
    suggestionChipHover: {
      background: isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb",
      borderColor: isDark ? "#FF6600" : "#FF6600",
      transform: "translateY(-1px)",
    },
    typingIndicator: {
      alignSelf: "flex-start",
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "#f8f9fa",
      border: `1px solid ${isDark ? "rgba(255, 102, 0, 0.2)" : "#e5e7eb"}`,
      borderRadius: "18px 18px 18px 4px",
      padding: "8px 12px",
      display: "flex",
      alignItems: "center",
      gap: 6,
      maxWidth: "80%",
    },
    typingText: {
      fontSize: 13,
      color: isDark ? "#FF6600" : "#FF6600",
      fontStyle: "italic",
    },
    loadingDots: {
      display: "flex",
      gap: 3,
    },
    loadingDot: {
      width: 6,
      height: 6,
      background: isDark ? "#FF6600" : "#FF6600",
      borderRadius: "50%",
      animation: "typing 1.4s infinite",
    },
    form: {
      background: isDark ? "rgba(255, 102, 0, 0.05)" : "rgba(255, 102, 0, 0.05)",
      border: `2px solid ${isDark ? "rgba(255, 102, 0, 0.3)" : "rgba(255, 102, 0, 0.3)"}`,
      borderRadius: 12,
      padding: 12,
      marginTop: 8,
      animation: "slideIn 0.3s ease-out",
    },
    formHeader: {
      color: isDark ? "#FF6600" : "#FF6600",
      fontWeight: 600,
      fontSize: 14,
      marginBottom: 8,
    },
    formField: {
      marginBottom: 10,
    },
    formLabel: {
      display: "block",
      marginBottom: 4,
      fontSize: 12,
      fontWeight: 500,
      color: isDark ? "#cccccc" : "#374151",
    },
    formLabelRequired: {
      color: "#FF6600",
    },
    formInput: {
      width: "100%",
      padding: "8px 10px",
      border: `2px solid ${isDark ? "rgba(255, 102, 0, 0.4)" : "#d1d5db"}`,
      borderRadius: 6,
      background: isDark ? "rgba(2, 1, 2, 0.6)" : "#fff",
      color: isDark ? "#fff" : "#111",
      fontSize: 13,
      outline: "none",
      transition: "all 0.3s ease",
      fontFamily: "inherit",
    },
    formInputFocus: {
      borderColor: "#FF6600",
      boxShadow: "0 0 8px rgba(255, 102, 0, 0.3)",
      background: isDark ? "rgba(2, 1, 2, 0.8)" : "#fff",
    },
    formActions: {
      display: "flex",
      gap: 8,
      marginTop: 12,
    },
    formSubmit: {
      flex: 1,
      padding: "8px 16px",
      background: isDark ? "linear-gradient(135deg, #FF6600, #FF8533)" : "linear-gradient(135deg, #FF6600, #FF8533)",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 13,
      transition: "all 0.3s ease",
    },
    formSubmitHover: {
      background: isDark ? "linear-gradient(135deg, #FF8533, #FFAA66)" : "linear-gradient(135deg, #FF8533, #FFAA66)",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(255, 102, 0, 0.4)",
    },
    formSubmitDisabled: {
      background: isDark ? "rgba(255, 102, 0, 0.3)" : "rgba(255, 102, 0, 0.3)",
      cursor: "not-allowed",
      transform: "none",
      boxShadow: "none",
    },
    formCancel: {
      padding: "8px 16px",
      background: isDark ? "rgba(255, 255, 255, 0.1)" : "#6b7280",
      border: `2px solid ${isDark ? "rgba(255, 255, 255, 0.2)" : "#6b7280"}`,
      borderRadius: 6,
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 13,
      transition: "all 0.3s ease",
    },
    formCancelHover: {
      background: isDark ? "rgba(255, 255, 255, 0.2)" : "#5a6268",
      borderColor: isDark ? "rgba(255, 255, 255, 0.4)" : "#5a6268",
    },
    welcomeMessage: {
      fontSize: 12,
      color: isDark ? "#cccccc" : "#6b7280",
      textAlign: "center",
      padding: "16px 0",
      fontStyle: "italic",
    },
  };

  // Add CSS for animations and markdown styling
  const cssAnimations = `
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes typing {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-6px); opacity: 1; }
    }
    .chat-message h1, .chat-message h2, .chat-message h3 {
      font-size: 1em;
      font-weight: 600;
      margin: 0.3em 0 0.2em 0;
    }
    .chat-message p {
      margin: 0.2em 0;
    }
    .chat-message ul, .chat-message ol {
      margin: 0.2em 0;
      padding-left: 1em;
    }
    .chat-message li {
      margin: 0.1em 0;
    }
    .chat-message code {
      background: ${isDark ? "#333" : "#f1f5f9"};
      padding: 1px 3px;
      border-radius: 3px;
      font-size: 0.9em;
    }
    .chat-message pre {
      background: ${isDark ? "#333" : "#f8fafc"};
      padding: 6px;
      border-radius: 4px;
      overflow-x: auto;
      margin: 0.3em 0;
    }
    .chat-messages::-webkit-scrollbar {
      width: 6px;
    }
    .chat-messages::-webkit-scrollbar-track {
      background: ${isDark ? "rgba(255, 102, 0, 0.1)" : "#e5e7eb"};
      border-radius: 3px;
    }
    .chat-messages::-webkit-scrollbar-thumb {
      background: ${isDark ? "rgba(255, 102, 0, 0.5)" : "#FF6600"};
      border-radius: 3px;
    }
    .chat-messages::-webkit-scrollbar-thumb:hover {
      background: ${isDark ? "#FF6600" : "#FF8533"};
    }
  `;

  return (
    <div style={styles.root}>
      <style>{cssAnimations}</style>

      <div ref={listRef} style={styles.list} className="chat-messages">
        {messages.length === 0 && (
          <div style={styles.welcomeMessage}>
            Welcome to iForge! Ask about our 3D printing products, academy courses, or support requests.
          </div>
        )}

        {messages.map((m, i) => {
          const bubbleStyle = m.role === "user" ? styles.bubbleUser : { ...styles.bubbleBot, ...(m.ticketId ? styles.successMessage : {}) };
          return (
            <div key={i} style={{ ...styles.message, ...bubbleStyle }}>
              <div
                className="chat-message"
                style={{ whiteSpace: "pre-wrap" }}
                dangerouslySetInnerHTML={renderMarkdown(m.content)}
              />
              {m.ticketId && (
                <div style={styles.ticketId}>
                  <strong>Ticket ID:</strong> {m.ticketId}
                </div>
              )}
              {m.role === "assistant" && Array.isArray(m.sources) && m.sources.length > 0 && (
                <div style={styles.sourcesWrap}>
                  <strong style={{ fontSize: 10, marginRight: 6 }}>Sources:</strong>
                  {m.sources.map((u, idx) => (
                    <a
                      key={`${u}-${idx}`}
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.source}
                      onMouseEnter={(e) => {
                        e.target.style.background = isDark ? "rgba(255, 102, 0, 0.2)" : "rgba(255, 102, 0, 0.2)";
                        e.target.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = isDark ? "rgba(255, 102, 0, 0.1)" : "rgba(255, 102, 0, 0.1)";
                        e.target.style.transform = "scale(1)";
                      }}
                    >
                      {u.length > 35 ? u.substring(0, 32) + "..." : u}
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {sending && (
          <div style={styles.typingIndicator}>
            <span style={styles.typingText}>Assistant is thinking</span>
            <div style={styles.loadingDots}>
              <div style={{ ...styles.loadingDot, animationDelay: "0s" }} />
              <div style={{ ...styles.loadingDot, animationDelay: "0.2s" }} />
              <div style={{ ...styles.loadingDot, animationDelay: "0.4s" }} />
            </div>
          </div>
        )}

        {collectingData && activeForm && FORM_TEMPLATES[activeForm.intent] && FORM_TEMPLATES[activeForm.intent][activeForm.role] ? (
          <div style={styles.form}>
            <div style={styles.formHeader}>
              {FORM_TEMPLATES[activeForm.intent][activeForm.role].title}
            </div>
            <p style={{ marginBottom: 10, color: isDark ? "#cccccc" : "#6b7280", fontSize: 12 }}>
              {FORM_TEMPLATES[activeForm.intent][activeForm.role].description}
            </p>
            {FORM_TEMPLATES[activeForm.intent][activeForm.role].fields.map((field) => (
              <div key={field.name} style={styles.formField}>
                <label style={{ ...styles.formLabel, ...(field.required ? styles.formLabelRequired : {}) }}>
                  {field.label}
                  {field.required && <span style={styles.formLabelRequired}> *</span>}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    name={field.name}
                    placeholder={field.placeholder}
                    onChange={handleFormChange}
                    required={field.required}
                    style={{ ...styles.formInput, minHeight: 60, resize: "vertical" }}
                    value={formData[field.name] || ""}
                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                    onBlur={(e) => Object.assign(e.target.style, { borderColor: isDark ? "rgba(255, 102, 0, 0.4)" : "#d1d5db", boxShadow: "none", background: isDark ? "rgba(2, 1, 2, 0.6)" : "#fff" })}
                  />
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    onChange={handleFormChange}
                    required={field.required}
                    style={styles.formInput}
                    value={formData[field.name] || ""}
                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                    onBlur={(e) => Object.assign(e.target.style, { borderColor: isDark ? "rgba(255, 102, 0, 0.4)" : "#d1d5db", boxShadow: "none", background: isDark ? "rgba(2, 1, 2, 0.6)" : "#fff" })}
                  />
                )}
              </div>
            ))}
            <div style={styles.formActions}>
              <button
                onClick={submitForm}
                disabled={sending}
                style={{ ...styles.formSubmit, ...(sending ? styles.formSubmitDisabled : {}) }}
                onMouseEnter={(e) => !sending && Object.assign(e.target.style, styles.formSubmitHover)}
                onMouseLeave={(e) => !sending && Object.assign(e.target.style, { background: isDark ? "linear-gradient(135deg, #FF6600, #FF8533)" : "linear-gradient(135deg, #FF6600, #FF8533)", transform: "none", boxShadow: "none" })}
              >
                {sending ? "Submitting..." : "Submit"}
              </button>
              <button
                onClick={cancelForm}
                style={styles.formCancel}
                onMouseEnter={(e) => Object.assign(e.target.style, styles.formCancelHover)}
                onMouseLeave={(e) => Object.assign(e.target.style, { background: isDark ? "rgba(255, 255, 255, 0.1)" : "#6b7280", borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#6b7280" })}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          collectingData && (
            <div style={styles.error}>
              Error: Invalid form configuration. Please try again.
            </div>
          )
        )}

        {err && <div style={styles.error}>{err}</div>}
      </div>

      <div style={styles.composer}>
        {suggestions.length > 0 && (
          <div style={styles.suggestions}>
            {suggestions.map((sug, i) => (
              <div
                key={i}
                style={styles.suggestionChip}
                onClick={() => sendMessage(sug.message)}
                onMouseEnter={(e) => Object.assign(e.target.style, styles.suggestionChipHover)}
                onMouseLeave={(e) => Object.assign(e.target.style, { background: isDark ? "rgba(255, 102, 0, 0.1)" : "#f3f4f6", borderColor: isDark ? "rgba(255, 102, 0, 0.3)" : "#e5e7eb", transform: "none" })}
              >
                {sug.text}
              </div>
            ))}
          </div>
        )}
        <div style={styles.row}>
          <textarea
            ref={inputRef}
            style={{
              ...styles.textarea,
              ...(sending || !input.trim() ? {} : styles.textareaFocus),
            }}
            rows={1}
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={sending}
            onFocus={(e) => Object.assign(e.target.style, styles.textareaFocus)}
            onBlur={(e) => Object.assign(e.target.style, { borderColor: isDark ? "rgba(255, 102, 0, 0.3)" : "#d1d5db", boxShadow: "none", background: isDark ? "rgba(255, 255, 255, 0.05)" : "#fff" })}
          />
          <button
            style={{
              ...styles.send,
              ...(sending || !input.trim() ? styles.sendDisabled : {}),
            }}
            onClick={() => sendMessage(input)}
            disabled={sending || !input.trim()}
          >
            {sending ? "..." : "Send"}
          </button>
        </div>
        <div style={styles.hint}>
          Enter to send • Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};